/**
 * TripChip Component
 * Small chip showing trip info in the calendar
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';

interface TripChipProps {
  tripId: number;
  qNumber: string;
  fromAirport?: string;
  toAirport?: string;
  status: string;
  onClick?: () => void;
}

const statusColors: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700',
  PENDING_REVIEW: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-400 dark:hover:bg-yellow-900/60',
  IN_PROGRESS: 'bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-400 dark:hover:bg-blue-900/60',
  COMPLETED: 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-400 dark:hover:bg-green-900/60',
  SUBMITTED: 'bg-purple-100 text-purple-800 hover:bg-purple-200 dark:bg-purple-900/40 dark:text-purple-400 dark:hover:bg-purple-900/60',
  APPROVED: 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-400 dark:hover:bg-green-900/60',
  PAID: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-400 dark:hover:bg-emerald-900/60',
  CANCELLED: 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/40 dark:text-red-400 dark:hover:bg-red-900/60',
};

export const TripChip: React.FC<TripChipProps> = ({
  tripId,
  qNumber,
  fromAirport,
  toAirport,
  status,
  onClick,
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(`/trips/${tripId}`);
    }
  };

  // Show route snippet if available
  const routeSnippet =
    fromAirport && toAirport ? `${fromAirport}\u2192${toAirport}` : null;

  return (
    <button
      onClick={handleClick}
      className={cn(
        'w-full px-2 py-1.5 rounded text-xs font-medium text-left truncate transition-colors',
        statusColors[status] || 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      )}
      title={`${qNumber}${routeSnippet ? ` - ${routeSnippet}` : ''}`}
    >
      <span className="font-semibold">{qNumber}</span>
      {routeSnippet && (
        <span className="block text-[10px] font-medium truncate">{routeSnippet}</span>
      )}
    </button>
  );
};

export default TripChip;
